import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../../components/ui/Pagination";

const EMPTY = [];

/** Tree → flat rows with the parent's name (table view). */
export const flattenWithParent = (nodes, parent = null, out = []) => {
  (nodes || EMPTY).forEach((n) => {
    out.push({ ...n, parentName: parent?.companyName || "" });
    flattenWithParent(n.children, n, out);
  });
  return out;
};

const COLUMNS = [
  { label: "Company", key: "companyName" },
  { label: "Type", key: "type" },
  { label: "Parent company", key: "parentName" },
  { label: "Location", key: "location" },
  { label: "Branches", key: "branchCount", num: true },
  { label: "Employees", key: "employeeCount", num: true },
  { label: "Status", key: "isActive" },
];

const valueOf = (r, key) => {
  if (key === "location") return [r.city, r.region].filter(Boolean).join(" ");
  if (key === "isActive") return r.isActive === false ? 1 : 0;
  return r[key] ?? "";
};

/**
 * Companies as a flat, sortable table (like Branches): search comes from the
 * chart toolbar; status filter, sort and paging are local.
 * actions(row) renders the row buttons; onOpen(row) opens the details.
 */
const CompanyTable = ({ nodes, query, actions, onOpen, yourCompanyId }) => {
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState({ key: "companyName", dir: "asc" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const rows = useMemo(() => {
    const q = (query || "").toLowerCase();
    const list = flattenWithParent(nodes).filter((r) => {
      if (status === "Active" && r.isActive === false) return false;
      if (status === "Inactive" && r.isActive !== false) return false;
      if (!q) return true;
      return `${r.companyName} ${r.type || ""} ${r.parentName} ${r.city || ""} ${r.region || ""}`
        .toLowerCase()
        .includes(q);
    });
    const col = COLUMNS.find((c) => c.key === sort.key);
    return list.sort((a, b) => {
      const va = valueOf(a, sort.key);
      const vb = valueOf(b, sort.key);
      const cmp = col?.num
        ? (Number(va) || 0) - (Number(vb) || 0)
        : String(va).localeCompare(String(vb), undefined, {
            sensitivity: "base",
          });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [nodes, query, status, sort]);

  useEffect(() => setPage(1), [query, status, sort]);

  const shown = rows.slice((page - 1) * limit, page * limit);
  const sortBy = (key) =>
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
    }));
  const sortIcon = (key) =>
    sort.key !== key ? (
      <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>
    ) : (
      <i
        className={`bi ${sort.dir === "asc" ? "bi-sort-down" : "bi-sort-up"} ms-2 text-primary fs-070`}
      ></i>
    );

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <select
          className="form-select form-select-sm w-auto"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Status"
        >
          <option value="All">All status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </div>
      <div className="glass-table-card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="custom-table-header sortable">
              <tr>
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.key}
                    className={`py-3 border-0 ${i === 0 ? "ps-4" : ""} ${c.num ? "text-end" : ""}`}
                    onClick={() => sortBy(c.key)}
                    aria-sort={
                      sort.key === c.key
                        ? sort.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                  >
                    {c.label.toUpperCase()} {sortIcon(c.key)}
                  </th>
                ))}
                <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {shown.map((r) => (
                <tr key={r.id} className="align-middle">
                  <td className="ps-4 py-3">
                    <button
                      type="button"
                      className="btn btn-link p-0 fw-bold fs-085 text-slate-800 text-decoration-none text-start"
                      onClick={() => onOpen(r)}
                    >
                      {r.companyName}
                    </button>
                    {r.id === yourCompanyId && (
                      <span className="chip chip--sm ms-2">Your company</span>
                    )}
                  </td>
                  <td className="fs-080 text-slate-700">{r.type || "—"}</td>
                  <td className="fs-080 text-slate-700">
                    {r.parentName || (
                      <span className="text-slate-400">Top level</span>
                    )}
                  </td>
                  <td className="fs-080 text-slate-700">
                    {[r.city, r.region].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="fs-080 text-slate-700 text-end tabular-nums">
                    {r.branchCount ?? 0}
                  </td>
                  <td
                    className="fs-080 text-slate-700 text-end tabular-nums"
                    title="Own / including sub-companies"
                  >
                    {r.employeeCount ?? 0} / {r.totalEmployeeCount ?? 0}
                  </td>
                  <td>
                    <span
                      className={`status-pill status-pill--${r.isActive === false ? "gray" : "green"}`}
                    >
                      {r.isActive === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="pe-4 text-end text-nowrap">
                    <div className="d-inline-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-light border"
                        onClick={() => onOpen(r)}
                      >
                        Details
                      </button>
                      {actions(r)}
                    </div>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="approval-empty">
                    No companies match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          limit={limit}
          total={rows.length}
          onPageChange={setPage}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </div>
    </>
  );
};

export default CompanyTable;
